/**
 * Analytics client — dual-tracks to:
 *  1. Google Analytics 4 (if VITE_GA_ID is set)
 *  2. Our own FastAPI backend (/api/analytics/track) — real-time, owner-controlled
 */

import ReactGA from "react-ga4";

const viteEnv = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
const GA_ID = viteEnv?.VITE_GA_ID ?? "";

// ── Session state (in-memory, refreshed on page load) ────────────────────────
let _sessionId: string | null = null;
let _email: string | null = null;
let _scrollListenerAttached = false;
let _maxScrollDepth = 0;

// ── GA init ───────────────────────────────────────────────────────────────────
export function initAnalytics() {
  if (GA_ID) ReactGA.initialize(GA_ID);
  void _initSession();
  _attachScrollTracker();
}

export function setAnalyticsUser(email: string) {
  _email = email;
}

// ── Legacy GA page tracking (kept for backward compat) ───────────────────────
export function trackPage(path: string) {
  if (GA_ID) ReactGA.send({ hitType: "pageview", page: path });
  void _track("page_view", path, {});
}

// ── Core trackEvent — unified API ────────────────────────────────────────────
/**
 * Track a named event.
 *
 * @param eventName  snake_case event name, e.g. "mcp_converted", "mcp_url_copied"
 * @param properties  optional key/value bag
 */
export function trackEvent(eventName: string, properties?: Record<string, unknown>) {
  if (GA_ID) ReactGA.event({ category: "app", action: eventName });
  void _track(eventName, _currentPage(), properties ?? {});
}

// ── Named event helpers ───────────────────────────────────────────────────────

/** Called right after a user successfully generates + hosts an MCP server */
export function trackConversion(props: { spec_title?: string; tool_count?: number; base_url?: string }) {
  trackEvent("mcp_converted", props);
}

/** Called when the user copies the MCP URL */
export function trackUrlCopy(mcpUrl: string) {
  trackEvent("mcp_url_copied", { url: mcpUrl });
}

/** Called when the user opens the MCP in a new tab */
export function trackMcpPreviewOpen(mcpUrl: string) {
  trackEvent("mcp_preview_open", { url: mcpUrl });
}

/** Called on spec upload / URL submit / paste */
export function trackSpecInput(method: "upload" | "url" | "paste", fileType?: string) {
  trackEvent(`spec_${method}`, { file_type: fileType });
}

/** Called when user clicks share button (Twitter/Reddit) */
export function trackShare(platform: "twitter" | "reddit") {
  trackEvent("share_click", { platform });
}

/** Called on sign-in / sign-up */
export function trackAuth(type: "sign_in" | "sign_up", email: string) {
  setAnalyticsUser(email);
  trackEvent(type, { email });
}

/** Called after successful Pro checkout (webhook may also record server-side). */
export function trackUserUpgraded(plan: string = "pro") {
  trackEvent("user_upgraded", { plan });
}

/** Called when ConnectPage tab is switched */
export function trackConnectTab(tab: string) {
  trackEvent("connect_tab_switch", { tab });
}

/** Called when a registry card is clicked */
export function trackRegistryCardClick(slug: string, name: string) {
  trackEvent("registry_card_click", { slug, name });
}

/** Called when an MCP is starred in the registry */
export function trackRegistryStar(slug: string, starred: boolean) {
  trackEvent("registry_star", { slug, starred });
}

/** Called on any frontend error */
export function trackError(message: string, context?: string) {
  trackEvent("error", { message: message.slice(0, 200), context });
}

// ── Internals ─────────────────────────────────────────────────────────────────

async function _initSession() {
  try {
    const params = new URLSearchParams(window.location.search);
    const body = {
      referrer: document.referrer || null,
      utm_source: params.get("utm_source"),
      utm_medium: params.get("utm_medium"),
      utm_campaign: params.get("utm_campaign"),
      email: _email,
    };
    const res = await fetch("/api/analytics/session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
    if (res.ok) {
      const data = (await res.json()) as { session_id: string };
      _sessionId = data.session_id;
    }
  } catch {
    // analytics should never break the app
  }
}

async function _track(
  eventName: string,
  page: string,
  properties: Record<string, unknown>,
) {
  try {
    await fetch("/api/analytics/track", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        event: eventName,
        page,
        properties,
        email: _email,
        session_id: _sessionId,
      }),
    });
  } catch {
    // silent
  }
}

function _currentPage(): string {
  return window.location.pathname + window.location.search;
}

function _attachScrollTracker() {
  if (_scrollListenerAttached || typeof window === "undefined") return;
  _scrollListenerAttached = true;

  const THRESHOLDS = [25, 50, 75, 90, 100];
  const _fired = new Set<number>();

  const onScroll = () => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (docHeight <= 0) return;
    const depth = Math.round((scrollTop / docHeight) * 100);
    if (depth > _maxScrollDepth) _maxScrollDepth = depth;

    for (const t of THRESHOLDS) {
      if (depth >= t && !_fired.has(t)) {
        _fired.add(t);
        void _track("scroll_depth", _currentPage(), { depth: t });
      }
    }
  };

  window.addEventListener("scroll", onScroll, { passive: true });

  // On page unload send final max depth
  window.addEventListener("beforeunload", () => {
    if (_maxScrollDepth > 0) {
      void _track("scroll_depth_final", _currentPage(), { depth: _maxScrollDepth });
    }
  });
}
